// Messaging API - Database-backed messaging system
import { turso } from '@/database/client';
import { moderateContent, logModerationAction, type UserContext } from './content-moderation';

export interface Message {
  id: string;
  from_user_id: string;
  to_user_id: string;
  message: string;
  is_read: boolean;
  created_at: string;
  is_admin_notification?: boolean;
  notification_type?: string;
}

export interface OnlineUser {
  id: string;
  email: string;
  name: string;
  is_online: boolean;
  show_online_status: boolean;
  last_active_at: string;
}

// Helper: fetch user by email
export async function getUserByEmail(email: string): Promise<OnlineUser | null> {
  try {
    const result = await turso.execute({
      sql: `SELECT id, email, name, show_online_status, last_active_at FROM users WHERE email = ? LIMIT 1`,
      args: [email]
    });

    if (!result.rows.length) return null;
    const row: any = result.rows[0];
    return {
      id: row.id,
      email: row.email,
      name: row.name,
      is_online: Boolean(row.show_online_status),
      show_online_status: Boolean(row.show_online_status),
      last_active_at: row.last_active_at
    };
  } catch (error) {
    console.error('Failed to get user by email:', error);
    return null;
  }
}

// Get all users who are showing as online (with age-based filtering)
export async function getOnlineUsers(currentUserId?: string): Promise<OnlineUser[]> {
  try {
    // Get current user's age group if provided
    let currentUserAgeGroup: string | null = null;
    if (currentUserId) {
      const ageData = await turso.execute({
        sql: "SELECT data_value FROM user_data WHERE user_id = ? AND data_type = 'age_verification' LIMIT 1",
        args: [currentUserId]
      });
      
      if (ageData.rows && ageData.rows.length > 0) {
        try {
          const restrictions = JSON.parse(ageData.rows[0].data_value as string);
          currentUserAgeGroup = restrictions.ageGroup || null;
        } catch (e) {
          console.error('Failed to parse age restrictions:', e);
        }
      }
    }

    const result = await turso.execute(`
      SELECT id, email, name, show_online_status, last_active_at
      FROM users
      WHERE show_online_status = 1
      ORDER BY name
    `);

    const allUsers = result.rows.map((row: any) => ({
      id: row.id,
      email: row.email,
      name: row.name,
      is_online: true,
      show_online_status: Boolean(row.show_online_status),
      last_active_at: row.last_active_at
    }));

    // If current user is under 13 or teen, filter out adults
    if (currentUserAgeGroup === 'under13' || currentUserAgeGroup === 'teen') {
      const filteredUsers: OnlineUser[] = [];
      
      for (const user of allUsers) {
        // Get each user's age group
        const userAgeData = await turso.execute({
          sql: "SELECT data_value FROM user_data WHERE user_id = ? AND data_type = 'age_verification' LIMIT 1",
          args: [user.id]
        });
        
        if (userAgeData.rows && userAgeData.rows.length > 0) {
          try {
            const userRestrictions = JSON.parse(userAgeData.rows[0].data_value as string);
            const userAgeGroup = userRestrictions.ageGroup;
            
            // Only show users in same age group or younger
            // under13 can only see under13
            // teen can see under13 and teen
            if (currentUserAgeGroup === 'under13' && userAgeGroup === 'under13') {
              filteredUsers.push(user);
            } else if (currentUserAgeGroup === 'teen' && (userAgeGroup === 'under13' || userAgeGroup === 'teen')) {
              filteredUsers.push(user);
            }
          } catch (e) {
            console.error('Failed to parse user age restrictions:', e);
          }
        }
      }
      
      console.log(`[AGE FILTER] Current user (${currentUserAgeGroup}): Filtered ${allUsers.length} users to ${filteredUsers.length} age-appropriate users`);
      return filteredUsers;
    }

    // Adults can see everyone
    return allUsers;
  } catch (error) {
    console.error('Failed to get online users:', error);
    return [];
  }
}

// Update user's online status
export async function updateOnlineStatus(userId: string, showOnline: boolean): Promise<void> {
  try {
    await turso.execute({
      sql: `
        UPDATE users 
        SET show_online_status = ?, last_active_at = datetime('now')
        WHERE id = ?
      `,
      args: [showOnline ? 1 : 0, userId]
    });
  } catch (error) {
    console.error('Failed to update online status:', error);
  }
}

// Update user's last active timestamp
export async function updateLastActive(userId: string): Promise<void> {
  try {
    await turso.execute({
      sql: `UPDATE users SET last_active_at = datetime('now') WHERE id = ?`,
      args: [userId]
    });
  } catch (error) {
    console.error('Failed to update last active:', error);
  }
}

// Send a message (with content moderation)
export async function sendMessage(
  fromUserId: string, 
  toUserId: string, 
  messageText: string,
  userContext?: UserContext
): Promise<{ success: boolean; message?: Message; error?: string; moderationResult?: any }> {
  try {
    // If user context provided, moderate the content
    if (userContext) {
      const moderationResult = moderateContent(messageText, userContext);
      
      // Log the moderation action
      await logModerationAction(fromUserId, messageText, moderationResult, 'direct_message');
      
      // Block message if not allowed
      if (!moderationResult.allowed) {
        return {
          success: false,
          error: moderationResult.reason || 'Message blocked by content filter',
          moderationResult
        };
      }
    }
    
    const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    await turso.execute({
      sql: `
        INSERT INTO messages (id, from_user_id, to_user_id, message, is_read, created_at)
        VALUES (?, ?, ?, ?, 0, datetime('now'))
      `,
      args: [messageId, fromUserId, toUserId, messageText]
    });

    // Fetch the created message
    const result = await turso.execute({
      sql: `SELECT * FROM messages WHERE id = ?`,
      args: [messageId]
    });

    if (result.rows.length > 0) {
      const row: any = result.rows[0];
      return {
        success: true,
        message: {
          id: row.id,
          from_user_id: row.from_user_id,
          to_user_id: row.to_user_id,
          // Normalize to string so React never tries to render an object
          message: typeof row.message === 'string' ? row.message : JSON.stringify(row.message),
          is_read: Boolean(row.is_read),
          created_at: row.created_at,
          is_admin_notification: Boolean(row.is_admin_notification),
          notification_type: row.notification_type
        }
      };
    }

    return { success: false, error: 'Failed to create message' };
  } catch (error) {
    console.error('Failed to send message:', error);
    return { success: false, error: 'Failed to send message' };
  }
}

// Get messages between two users
export async function getConversationMessages(userId1: string, userId2: string): Promise<Message[]> {
  try {
    const result = await turso.execute({
      sql: `
        SELECT * FROM messages 
        WHERE (from_user_id = ? AND to_user_id = ?)
           OR (from_user_id = ? AND to_user_id = ?)
        ORDER BY created_at ASC
      `,
      args: [userId1, userId2, userId2, userId1]
    });

    return result.rows.map((row: any) => ({
      id: row.id,
      from_user_id: row.from_user_id,
      to_user_id: row.to_user_id,
      message: typeof row.message === 'string' ? row.message : JSON.stringify(row.message),
      is_read: Boolean(row.is_read),
      created_at: row.created_at,
      is_admin_notification: Boolean(row.is_admin_notification),
      notification_type: row.notification_type
    }));
  } catch (error) {
    console.error('Failed to get conversation messages:', error);
    return [];
  }
}

// Mark messages as read
export async function markMessagesAsRead(userId: string, otherUserId: string): Promise<void> {
  try {
    await turso.execute({
      sql: `
        UPDATE messages 
        SET is_read = 1 
        WHERE to_user_id = ? AND from_user_id = ? AND is_read = 0
      `,
      args: [userId, otherUserId]
    });
  } catch (error) {
    console.error('Failed to mark messages as read:', error);
  }
}

// Get unread message count for a user from a specific sender
export async function getUnreadCount(toUserId: string, fromUserId: string): Promise<number> {
  try {
    const result = await turso.execute({
      sql: `
        SELECT COUNT(*) as count 
        FROM messages 
        WHERE to_user_id = ? AND from_user_id = ? AND is_read = 0
      `,
      args: [toUserId, fromUserId]
    });

    if (result.rows.length > 0) {
      return Number((result.rows[0] as any).count) || 0;
    }
    return 0;
  } catch (error) {
    console.error('Failed to get unread count:', error);
    return 0;
  }
}

// Get total unread message count for a user
export async function getTotalUnreadCount(userId: string): Promise<number> {
  try {
    const result = await turso.execute({
      sql: `
        SELECT COUNT(*) as count 
        FROM messages 
        WHERE to_user_id = ? AND is_read = 0
      `,
      args: [userId]
    });

    if (result.rows.length > 0) {
      return Number((result.rows[0] as any).count) || 0;
    }
    return 0;
  } catch (error) {
    console.error('Failed to get total unread count:', error);
    return 0;
  }
}

// Get unread counts grouped by sender for a recipient
export async function getUnreadCountsBySender(recipientId: string): Promise<Record<string, number>> {
  const counts: Record<string, number> = {};
  try {
    const result = await turso.execute({
      sql: `
        SELECT from_user_id AS sender, COUNT(*) as count
        FROM messages
        WHERE to_user_id = ? AND is_read = 0
        GROUP BY from_user_id
      `,
      args: [recipientId]
    });

    for (const row of result.rows as any[]) {
      if (row.sender) {
        counts[String(row.sender)] = Number(row.count) || 0;
      }
    }
  } catch (error) {
    console.error('Failed to get unread counts by sender:', error);
  }
  return counts;
}

// ==========================================
// CONTACTS / FRIENDS LIST MANAGEMENT
// ==========================================

// Add a contact to user's friends list
export async function addContact(
  userId: string,
  contactUserId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    await turso.execute({
      sql: `INSERT INTO user_contacts (user_id, contact_user_id, status)
            VALUES (?, ?, 'active')`,
      args: [userId, contactUserId]
    });
    console.log(`✅ Contact added: ${userId} -> ${contactUserId}`);
    return { success: true };
  } catch (error: any) {
    console.error('Failed to add contact:', error);
    if (error.message?.includes('UNIQUE')) {
      return { success: false, error: 'Contact already added' };
    }
    return { success: false, error: 'Failed to add contact' };
  }
}

// Remove a contact from user's friends list
export async function removeContact(
  userId: string,
  contactUserId: string
): Promise<{ success: boolean }> {
  try {
    await turso.execute({
      sql: `DELETE FROM user_contacts WHERE user_id = ? AND contact_user_id = ?`,
      args: [userId, contactUserId]
    });
    console.log(`✅ Contact removed: ${userId} -> ${contactUserId}`);
    return { success: true };
  } catch (error) {
    console.error('Failed to remove contact:', error);
    return { success: false };
  }
}

// Get user's contacts list (people they've added)
export async function getUserContacts(userId: string): Promise<OnlineUser[]> {
  try {
    const result = await turso.execute({
      sql: `SELECT u.id, u.email, u.name, u.show_online_status, u.last_active_at
            FROM users u
            INNER JOIN user_contacts uc ON u.id = uc.contact_user_id
            WHERE uc.user_id = ? AND uc.status = 'active'
            ORDER BY u.name`,
      args: [userId]
    });

    return result.rows.map((row: any) => ({
      id: row.id,
      email: row.email,
      name: row.name,
      is_online: Boolean(row.show_online_status),
      show_online_status: Boolean(row.show_online_status),
      last_active_at: row.last_active_at
    }));
  } catch (error) {
    console.error('Failed to get user contacts:', error);
    return [];
  }
}

// Get all available users (not yet in contacts) for adding
export async function getAvailableUsers(
  currentUserId: string
): Promise<OnlineUser[]> {
  try {
    const result = await turso.execute({
      sql: `SELECT u.id, u.email, u.name, u.show_online_status, u.last_active_at
            FROM users u
            WHERE u.id != ?
            AND u.id NOT IN (
              SELECT contact_user_id FROM user_contacts 
              WHERE user_id = ?
            )
            ORDER BY u.name`,
      args: [currentUserId, currentUserId]
    });

    return result.rows.map((row: any) => ({
      id: row.id,
      email: row.email,
      name: row.name,
      is_online: Boolean(row.show_online_status),
      show_online_status: Boolean(row.show_online_status),
      last_active_at: row.last_active_at
    }));
  } catch (error) {
    console.error('Failed to get available users:', error);
    return [];
  }
}

// Get online users filtered by contacts list
export async function getOnlineContactsOnly(currentUserId: string): Promise<OnlineUser[]> {
  try {
    // Get contacts who are showing as online
    const contacts = await getUserContacts(currentUserId);
    return contacts.filter(c => c.show_online_status);
  } catch (error) {
    console.error('Failed to get online contacts:', error);
    return [];
  }
}

// Check if user A has user B in their contacts
export async function isContact(userId: string, contactUserId: string): Promise<boolean> {
  try {
    const result = await turso.execute({
      sql: `SELECT 1 FROM user_contacts WHERE user_id = ? AND contact_user_id = ? AND status = 'active' LIMIT 1`,
      args: [userId, contactUserId]
    });
    return result.rows.length > 0;
  } catch (error) {
    console.error('Failed to check contact status:', error);
    return false;
  }
}

